const WIDGET_SCRIPT_ID = "horaizen-help-widget-script";
const WIDGET_CONTAINER_ID = "horaizen-help-widget";
const WIDGET_READY_TIMEOUT_MS = 10_000;
const SUPPORT_OPEN_GUARD_MS = 600;
let lastSupportOpenAttemptMs = 0;

/**
 * Waits for the widget container element to appear in the DOM (third-party script creates it after load).
 * @param {AbortSignal} [signal] - Optional abort signal to cancel waiting.
 * @returns {Promise<boolean>} Resolves with true when container exists, false if aborted or timed out.
 */
function waitForWidgetContainer(signal) {
  if (document.getElementById(WIDGET_CONTAINER_ID)) return Promise.resolve(true);
  return new Promise((resolve) => {
    const deadline = Date.now() + WIDGET_READY_TIMEOUT_MS;
    const timer = setInterval(() => {
      if (signal?.aborted || document.getElementById(WIDGET_CONTAINER_ID)) {
        clearInterval(timer);
        resolve(signal?.aborted ? false : true);
      } else if (Date.now() >= deadline) {
        clearInterval(timer);
        console.warn("[injectHoraizenHelpWidget] Widget container did not appear within timeout");
        resolve(false);
      }
    }, 50);
  });
}

/**
 * Injects the Horaizen help widget script and sets window.WidgetConfig.
 * Resolves when the script has loaded and the widget container exists.
 * Does not run if userId or customer are missing; returns a resolved promise in that case.
 * @param {string} userId - Current user identifier.
 * @param {string|number} customer - Customer identifier.
 * @param {{ signal?: AbortSignal }} [options] - Optional abort signal.
 * @returns {Promise<void>} Resolves when widget is ready; rejects on script load error.
 */
export function injectHoraizenHelpWidget(userId, customer, options = {}) {
  if (!userId || !customer) return Promise.resolve();
  removeHoraizenHelpWidget();
  const { signal } = options;
  if (signal?.aborted) return Promise.resolve();

  // SECURITY: Set config in JS so it executes; inline script text on dynamic scripts is unreliable.
  // REQ-WIDGET-1: customer and user_id are required by the Horaizen vendor contract; leave as-is.
  window.WidgetConfig = { customer, user_id: userId, menu_option: "" };

  const widgetScript = document.createElement("script");
  widgetScript.id = WIDGET_SCRIPT_ID;
  widgetScript.src = `https://support.horaizen.io/widget-loader.js?t=${Date.now()}`;

  return new Promise((resolve, reject) => {
    const onAbort = () => {
      widgetScript.onload = widgetScript.onerror = null;
      resolve();
    };
    signal?.addEventListener("abort", onAbort, { once: true });

    widgetScript.onload = () => {
      signal?.removeEventListener("abort", onAbort);
      console.log("[injectHoraizenHelpWidget] horaizen help widget script loaded");
      waitForWidgetContainer(signal).then((ready) => {
        if (ready) console.log("[injectHoraizenHelpWidget] widget container ready");
        resolve();
      });
    };
    widgetScript.onerror = () => {
      signal?.removeEventListener("abort", onAbort);
      console.warn("[injectHoraizenHelpWidget] horaizen help widget script failed to load");
      reject(new Error("Horaizen help widget script failed to load"));
    };
    document.body.appendChild(widgetScript);
  });
}

/**
 * Removes the widget script and container so re-injection can run cleanly.
 */
export function removeHoraizenHelpWidget() {
  document.getElementById(WIDGET_SCRIPT_ID)?.remove();
  document.getElementById(WIDGET_CONTAINER_ID)?.remove();
  if (typeof window !== "undefined" && "WidgetConfig" in window) delete window.WidgetConfig;
}

export function showHoraizenHelpWidget() {
  const widget = document.getElementById(WIDGET_CONTAINER_ID);
  const button = document.querySelector(".hw-bubble");
  if (!widget) {
    console.log("[showHoraizenHelpWidget] Widget not found");
    return;
  }
  button?.style.setProperty("bottom", "9vh", "important");
  widget.style.setProperty("display", "block", "important");
  console.log("[AuthenticatedLayout] Widget found and displayed");
}

/**
 * Opens the support panel from the floating help widget bubble.
 * Safe no-op when the widget is not mounted yet.
 */
export function openHoraizenHelpSupport() {
  const now = Date.now();
  if (now - lastSupportOpenAttemptMs < SUPPORT_OPEN_GUARD_MS) return;
  lastSupportOpenAttemptMs = now;

  showHoraizenHelpWidget();
  const button = document.querySelector(".hw-bubble");
  if (!button) {
    console.log("[openHoraizenHelpSupport] Bubble button not found");
    return;
  }
  button.click();
}
